import type {
  UsageRequestLogEntry,
  UsageRequestTokenBilling,
  UsageRequestUnitBilling,
  UsageRequestUnitBillingChargedEntry,
} from '@nadoutong/contracts';
import { Check, Copy, Search } from 'lucide-react';
import type { FormEvent } from 'react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { localizeCaughtError } from '../api/errorMessages.ts';
import { clipboardErrorKey, useClipboard } from '../hooks/useClipboard.tsx';
import { useDisplayRate } from '../api/useBilling.ts';
import { useKeysList } from '../api/useKeys.ts';
import { useUsageRequestDetail, useUsageRequests } from '../api/useUsage.ts';
import { apiKeyDisplayName } from '../components/apiKeyName.ts';
import {
  cnyRateDecimalsForCard,
  formatCnyRateFromUsdRate,
  formatTimestamp,
  formatTokens,
  formatUsdMicrosAsCnyMeteredShare,
  formatUsdMicrosOrCentsAsCnyMetered,
  isUnitMeteredProtocol,
  requestBillingModality,
  unitAwareTokenCell,
  usdMicrosFromCentsFallback,
} from '../components/formatters.ts';
import { PanelError } from '../components/PanelError.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { ExportExcelButton } from '../export/ExportButton.tsx';
import { isValidExportRate } from '../export/money.ts';
import { exportRequestLogs } from '../export/run.ts';

import { canAccessApiKeys, hasPermission, useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  buttonClass,
  Chip,
  DateRangePicker,
  EmptyState,
  Input,
  PageHeader,
  Panel,
} from '../ui/index.ts';
import { RequestModalityIcon } from './RequestModalityIcon.tsx';
import { adminRangePresets } from './usage/AdminFilterBar.tsx';
import { dayRangeToIsoWindow, resolveRangeSelection, todayLocalDay } from './usage/format.ts';
import type { Translate } from './usage/shared.tsx';
import { MAX_RANGE_SPAN_DAYS } from './usage/shared.tsx';

/**
 * `/requests` — request log explorer (epic #950 PR-5, Field Notes
 * reskin of the local page). Wire behavior preserved: DateRangePicker
 * (7d / 30d / 90d / month presets plus a custom window, same widget
 * as the admin usage page), request-id / key / status filters, 50-row
 * paging, and the click-to-expand technical detail row.
 */

const PAGE_SIZE = 50;

const SELECT_CLASS =
  'h-9 w-full rounded border border-line bg-raised px-3 text-body text-ink transition-colors duration-150 hover:border-line-strong focus:border-forest focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/30 disabled:pointer-events-none disabled:opacity-60';

/** Preset applied when `?range=` is absent or unrecognisable. */
const DEFAULT_RANGE = '7d';

// Retired ids (`today`, and the even older `24h`) no longer have a
// matching shortcut; keep those deep links on the default 7-day window
// instead of treating them as unrecognised noise.
const RANGE_ALIASES: Readonly<Record<string, string>> = { '24h': '7d', today: '7d' };

/**
 * URL params are user-editable, so the status reader narrows rather
 * than trusts: anything the API would reject collapses to "all"
 * instead of being forwarded.
 */
function parseStatus(value: string | null): 'success' | 'error' | '' {
  return value === 'success' || value === 'error' ? value : '';
}

/**
 * The four filter dimensions (range, request id, key, status) live in
 * the URL rather than component state, so a filtered view is a link
 * that can be pasted into a ticket and restored exactly. Writes go
 * through `patchParams` with `replace`, because narrowing a filter
 * should not bury the previous page under one history entry per
 * change. Paging offset and the expanded row stay local: they describe
 * how the result set is being looked at, not which rows it contains.
 */
export function Requests(): JSX.Element {
  const t = useT();
  const { user, fetchImpl } = useSession();
  // `/usage` forks on this permission: without it the route renders the
  // SELF-scoped view, which has no key filter and ignores `?apiKeyId=`.
  // The drill-down is therefore only offered to sessions the
  // destination can actually serve.
  const canReadOrgUsage = hasPermission(user, 'org:usage:read');
  // The key filter is built straight off `/v1/keys`. Listing 403s
  // unless the session holds any of `API_KEYS_PERMISSIONS` (the same
  // set as the `/keys` nav row), so finance never fires the request
  // and the dropdown is hidden — they still read org-wide logs via
  // `org:usage:read`. When the list does run, `org:api_keys:read`
  // decides which of the two sets the "all keys" option claims to
  // mean (#14).
  const canReadKeys = canAccessApiKeys(user);
  const canReadOrgKeys = hasPermission(user, 'org:api_keys:read');
  const keysQ = useKeysList(canReadKeys);
  const displayRateQ = useDisplayRate();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = todayLocalDay();
  const rangePresets = useMemo(() => adminRangePresets(today, t), [today, t]);
  const rangeSelection = useMemo(
    () =>
      resolveRangeSelection({
        rangeParam: searchParams.get('range'),
        fromParam: searchParams.get('from'),
        toParam: searchParams.get('to'),
        presets: rangePresets,
        defaultPresetId: DEFAULT_RANGE,
        maxSpanDays: MAX_RANGE_SPAN_DAYS,
        today,
        aliases: RANGE_ALIASES,
      }),
    [searchParams, rangePresets, today],
  );
  /**
   * Whether the user NARROWED anything — which is not the same as
   * "the URL carries params". The range is always set (the page has no
   * "all time" window), so counting query params would mark every view
   * filtered and no account would ever see the integration path.
   *
   * The test is the RESOLVED preset, not the raw `?range=`: an explicit
   * `?range=7d` is the view the page hands you unasked, as are the
   * retired aliases `24h` and `today` (both fall back to 7d). A custom
   * window resolves to `presetId: null`, which is likewise not the
   * default and so counts as narrowed.
   */
  const hasNarrowedRange = rangeSelection.presetId !== DEFAULT_RANGE;
  const requestId = searchParams.get('requestId') ?? '';
  // Deliberately not validated against `keysQ`: the key list loads
  // asynchronously, so checking membership would clear a pasted
  // `?apiKeyId=` for every render before the list arrives.
  const apiKeyId = searchParams.get('apiKeyId') ?? '';
  const status = parseStatus(searchParams.get('status'));
  const hasFilters =
    hasNarrowedRange || requestId.length > 0 || apiKeyId.length > 0 || status !== '';

  const [requestIdInput, setRequestIdInput] = useState('');
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // The text box is an unsubmitted draft of `?requestId=`, not a second
  // source of truth. Without this the box sits empty on a pasted link
  // while the table below it is filtered — the page contradicting
  // itself about what it is showing.
  useEffect(() => {
    setRequestIdInput(requestId);
  }, [requestId]);

  const range = useMemo(() => dayRangeToIsoWindow(rangeSelection.range), [rangeSelection.range]);
  const requestsQ = useUsageRequests({
    ...range,
    requestId: requestId.length > 0 ? requestId : undefined,
    apiKeyId: apiKeyId.length > 0 ? apiKeyId : undefined,
    status: status === '' ? undefined : status,
    limit: PAGE_SIZE,
    offset,
    detail: 'summary',
  });
  const rows = requestsQ.data?.rows ?? [];
  const total = requestsQ.data?.total ?? 0;
  // Router 归档水合谓词未校验 archived 标记，活跃页也会带回 errors[]。
  // 控制台先止血：仅本页确有归档行时才展示页级提示。见 router issue：归档水合谓词未校验 archived 标记。
  const pageArchiveErrors = rows.some((row) => row.archived === true)
    ? (requestsQ.data?.errors ?? [])
    : [];
  const expandedRow = rows.find((row) => row.id === expandedId);
  const expandedArchivedRequestId = expandedRow?.archived === true ? expandedRow.requestId : null;
  const archiveDetailQ = useUsageRequestDetail(
    expandedArchivedRequestId,
    expandedArchivedRequestId !== null,
  );
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;

  /**
   * Single write path for the URL filters. A `null` or empty value
   * removes the param so an "all" selection leaves no trace, and every
   * write resets paging and collapses the open detail row — a filter
   * change reshapes the result set, and page 4 of the old one (or a row
   * that is no longer in it) means nothing in the new one.
   */
  function patchParams(patch: Readonly<Record<string, string | null>>): void {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null || value.length === 0) next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
    setOffset(0);
    setExpandedId(null);
  }

  function handleSearch(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    patchParams({ requestId: requestIdInput.trim() });
  }

  /**
   * Back to the view the page opens with. The range is reset alongside
   * the other three: it counts as a filter above, so leaving it pinned
   * would hand back a window the user never asked for and call it
   * "cleared".
   */
  function clearFilters(): void {
    patchParams({
      requestId: null,
      apiKeyId: null,
      status: null,
      range: null,
      from: null,
      to: null,
    });
    setRequestIdInput('');
  }

  return (
    <section data-testid="requests-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('requests.title')}
        sub={t('requests.subtitle')}
        testId="requests-header"
        actions={
          <>
            <DateRangePicker
              value={rangeSelection.range}
              onChange={(next, presetId) => {
                if (presetId !== null) patchParams({ range: presetId, from: null, to: null });
                else patchParams({ range: 'custom', from: next.from, to: next.to });
              }}
              presets={rangePresets}
              activePresetId={rangeSelection.presetId}
              maxSpanDays={MAX_RANGE_SPAN_DAYS}
              ariaLabel={t('requests.range.aria_label')}
              labels={{
                prevMonth: t('usage.range.prev_month'),
                nextMonth: t('usage.range.next_month'),
                presets: t('usage.range.presets_aria_label'),
              }}
              testId="requests-range-picker"
            />
            {user !== null ? (
              <ExportExcelButton
                testId="requests-export"
                disabled={!isValidExportRate(cnyPerUsd)}
                onExport={() =>
                  exportRequestLogs({
                    fetchImpl,
                    orgId: user.orgId,
                    query: {
                      from: range.from,
                      to: range.to,
                      requestId: requestId.length > 0 ? requestId : undefined,
                      apiKeyId: apiKeyId.length > 0 ? apiKeyId : undefined,
                      status: status === '' ? undefined : status,
                      detail: 'full',
                    },
                    rangeDays: rangeSelection.range,
                    cnyPerUsd,
                  })
                }
              />
            ) : null}
          </>
        }
      />

      <Panel caption={t('requests.title')} padded testId="requests-card">
        <div className="flex flex-col gap-4">
          <form
            className={
              canReadKeys
                ? 'grid gap-4 lg:grid-cols-[minmax(220px,1.4fr)_minmax(180px,1fr)_minmax(150px,0.7fr)_auto]'
                : 'grid gap-4 lg:grid-cols-[minmax(220px,1.4fr)_minmax(150px,0.7fr)_auto]'
            }
            onSubmit={handleSearch}
          >
            <label className="flex flex-col gap-1.5">
              <span className="text-label text-ink-mid">{t('requests.filter.request_id')}</span>
              <Input
                mono
                value={requestIdInput}
                onChange={(e) => setRequestIdInput(e.currentTarget.value)}
                placeholder={t('requests.filter.request_id_placeholder')}
                data-testid="requests-filter-request-id"
              />
            </label>
            {canReadKeys ? (
              <label className="flex flex-col gap-1.5">
                <span className="text-label text-ink-mid">{t('requests.filter.key')}</span>
                <select
                  value={apiKeyId}
                  onChange={(e) => {
                    patchParams({ apiKeyId: e.currentTarget.value });
                  }}
                  data-testid="requests-filter-key"
                  className={SELECT_CLASS}
                >
                  <option value="">
                    {t(canReadOrgKeys ? 'requests.filter.key_all_org' : 'requests.filter.key_all')}
                  </option>
                  {(keysQ.data?.keys ?? []).map((key) => (
                    <option key={key.id} value={key.id}>
                      {apiKeyDisplayName(key.name, key.prefix, t)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="flex flex-col gap-1.5">
              <span className="text-label text-ink-mid">{t('requests.filter.status')}</span>
              <select
                value={status}
                onChange={(e) => {
                  patchParams({ status: e.currentTarget.value });
                }}
                data-testid="requests-filter-status"
                className={SELECT_CLASS}
              >
                <option value="">{t('requests.filter.status_all')}</option>
                <option value="success">{t('requests.filter.status_success')}</option>
                <option value="error">{t('requests.filter.status_error')}</option>
              </select>
            </label>
            <div className="flex items-end gap-2">
              <Button type="submit" variant="primary" data-testid="requests-filter-submit">
                <Search aria-hidden="true" className="h-3.5 w-3.5" />
                {t('requests.filter.submit')}
              </Button>
              {requestId.length > 0 ? (
                <Button
                  variant="secondary"
                  data-testid="requests-filter-clear"
                  onClick={() => {
                    patchParams({ requestId: null });
                    setRequestIdInput('');
                  }}
                >
                  {t('requests.filter.clear')}
                </Button>
              ) : null}
            </div>
          </form>

          {requestsQ.isLoading ? (
            <div className="flex flex-col gap-2" data-testid="requests-loading">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
              <span className="sr-only">{t('requests.loading')}</span>
            </div>
          ) : requestsQ.isError ? (
            <PanelError
              message={localizeCaughtError(requestsQ.error, t, 'requests.error')}
              retryLabel={t('requests.retry')}
              onRetry={() => {
                void requestsQ.refetch();
              }}
              testId="requests-error"
            />
          ) : rows.length === 0 && hasFilters ? (
            <EmptyState
              testId="requests-empty-filtered"
              title={t('requests.empty.filtered.title')}
              hint={t('requests.empty.filtered.hint')}
              action={
                <Button variant="secondary" onClick={clearFilters}>
                  {t('requests.filter.clear')}
                </Button>
              }
            />
          ) : rows.length === 0 ? (
            <EmptyState
              testId="requests-empty"
              title={t('requests.empty.title')}
              hint={t('requests.empty.hint')}
              action={
                <Link to="/docs" className={buttonClass('secondary')}>
                  {t('requests.empty.docs')}
                </Link>
              }
            />
          ) : (
            <>
              {pageArchiveErrors.length > 0 ? (
                <div
                  role="alert"
                  data-testid="requests-archive-errors"
                  className="flex flex-col gap-1"
                >
                  <p className="m-0 text-caption text-bad">{t('requests.archived.page_errors')}</p>
                  <p className="m-0 font-mono text-caption text-ink-low">
                    {pageArchiveErrors.join(' · ')}
                  </p>
                </div>
              ) : null}
              <RequestTable
                rows={rows}
                expandedId={expandedId}
                archiveDetailQ={archiveDetailQ}
                cnyPerUsd={cnyPerUsd}
                canReadOrgUsage={canReadOrgUsage}
                canFilterByKey={canReadKeys}
                onToggle={(id) => setExpandedId((current) => (current === id ? null : id))}
                onFilterByKey={(id) => patchParams({ apiKeyId: id })}
              />
              <footer className="flex flex-wrap items-center justify-between gap-3 text-caption text-ink-low">
                <span data-testid="requests-page-count">
                  {t('requests.pagination.summary', {
                    from: offset + 1,
                    to: Math.min(offset + PAGE_SIZE, total),
                    total,
                  })}
                </span>
                <div className="inline-flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!canPrev}
                    onClick={() => {
                      setOffset(Math.max(0, offset - PAGE_SIZE));
                      setExpandedId(null);
                    }}
                    data-testid="requests-page-prev"
                  >
                    {t('requests.pagination.prev')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!canNext}
                    onClick={() => {
                      setOffset(offset + PAGE_SIZE);
                      setExpandedId(null);
                    }}
                    data-testid="requests-page-next"
                  >
                    {t('requests.pagination.next')}
                  </Button>
                </div>
              </footer>
            </>
          )}
        </div>
      </Panel>
    </section>
  );
}

/**
 * Under async rating (#1410 P3) a request is visible before its price is
 * known, so rendering the formatted zero would tell a paying customer the
 * request was free minutes before the charge lands.
 *
 * `ratingStatus` is optional on the wire: a gateway that predates P3, or the
 * one-deploy window where platform has shipped and the gateway has not, omits
 * it. Every such row is final — hence `'rated'` as the fallback, never
 * `'pending'`, which would paint the entire history as unsettled.
 */
function isRatingPending(row: UsageRequestLogEntry): boolean {
  return (row.ratingStatus ?? 'rated') === 'pending';
}

/**
 * "Did this cost me anything?" has three answers, not two: zero cost
 * means "settled, and free" only once rating has finished. Reporting a
 * pending row as `not_billed` tells a paying customer no charge is
 * coming minutes before it lands, which is the same mistake the cost
 * cell already guards against — kept in one helper so the two places
 * cannot drift.
 */
function billingState(row: UsageRequestLogEntry): 'pending' | 'billed' | 'not_billed' {
  if (isRatingPending(row)) return 'pending';
  return usdMicrosFromCentsFallback(row.costUsdMicros, row.costUsdCents) > 0
    ? 'billed'
    : 'not_billed';
}

/**
 * A bare `429` is upstream vocabulary; the category is the part a
 * tenant can act on — back off, fix the request, or open a ticket.
 */
function statusCategory(statusCode: number, t: Translate): string {
  if (statusCode === 429) return t('requests.status_category.rate_limited');
  if (statusCode >= 500) return t('requests.status_category.service_error');
  if (statusCode >= 400) return t('requests.status_category.request_error');
  return t('requests.status_category.success');
}

/** #1670 token-column discriminator; the shared logic lives in formatters. */
function tokenCell(row: UsageRequestLogEntry, count: number): string {
  return unitAwareTokenCell(row, formatTokens(count));
}

/** `3 images · ¥x` / `Not delivered — not charged` unit billing summary. */
function unitBillingSummary(
  unitBilling: UsageRequestUnitBilling,
  cnyPerUsd: number | null,
  t: Translate,
): string {
  if (unitBilling.kind === 'not_charged') {
    return t(`requests.unit_billing.not_charged.${unitBilling.reason}`);
  }
  return unitBilling.entries
    .map((entry) =>
      t('requests.unit_billing.charged', {
        quantity: entry.quantity,
        unit: t(`requests.unit_billing.unit.${entry.unit}`),
        amount: formatUsdMicrosOrCentsAsCnyMetered(entry.amountUsdMicros, 0, cnyPerUsd),
      }),
    )
    .join(' + ');
}

/**
 * Per-dimension token charge table. Amounts are the stored shares —
 * never `tokens × perMtokUsd × rateMultiplier`. `sourceDimension` is
 * forensic and is not rendered.
 */
function TokenBillingDetail({
  billing,
  cnyPerUsd,
  rowId,
}: {
  readonly billing: UsageRequestTokenBilling;
  readonly cnyPerUsd: number | null;
  readonly rowId: string;
}): JSX.Element {
  const t = useT();
  const rateDecimals = cnyRateDecimalsForCard(
    cnyPerUsd !== null && Number.isFinite(cnyPerUsd) && cnyPerUsd > 0
      ? billing.entries.map((entry) => entry.perMtokUsd * cnyPerUsd)
      : [],
  );
  return (
    <div className="mt-3" data-testid={`requests-token-billing-${rowId}`}>
      <table className="w-full max-w-2xl border-collapse text-caption">
        <thead>
          <tr className="border-b border-line text-left">
            <th className="px-2 py-1 font-mono text-mono-label font-medium uppercase text-ink-low">
              {t('requests.token_billing.column.dimension')}
            </th>
            <th className="px-2 py-1 font-mono text-mono-label font-medium uppercase text-ink-low">
              {t('requests.token_billing.column.quantity')}
            </th>
            <th className="px-2 py-1 font-mono text-mono-label font-medium uppercase text-ink-low">
              {t('requests.token_billing.column.list_rate')}
            </th>
            <th className="px-2 py-1 font-mono text-mono-label font-medium uppercase text-ink-low">
              {t('requests.token_billing.column.amount')}
            </th>
          </tr>
        </thead>
        <tbody>
          {billing.entries.map((entry, index) => (
            <tr
              key={`${entry.dimension}-${index}`}
              className="border-b border-line-soft last:border-b-0"
              data-testid={`requests-token-billing-row-${rowId}-${entry.dimension}`}
            >
              <td className="px-2 py-1 text-ink">
                {t(`requests.token_billing.dimension.${entry.dimension}`)}
              </td>
              <td className="px-2 py-1 font-mono text-ink-mid">{formatTokens(entry.tokens)}</td>
              <td className="px-2 py-1 font-mono text-ink-mid">
                {formatCnyRateFromUsdRate(entry.perMtokUsd, cnyPerUsd, rateDecimals)}
                <span className="text-ink-low">{` ($${entry.perMtokUsd})`}</span>
              </td>
              <td className="px-2 py-1 font-mono text-forest">
                {formatUsdMicrosAsCnyMeteredShare(entry.amountUsdMicros, cnyPerUsd)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {billing.rateMultiplier !== 1 ? (
        <p
          className="m-0 mt-2 font-mono text-mono-label text-ink-low"
          data-testid={`requests-token-billing-multiplier-${rowId}`}
        >
          {t('requests.token_billing.multiplier', {
            multiplier: String(billing.rateMultiplier),
          })}
        </p>
      ) : null}
    </div>
  );
}

/** Additive unit shares on a token-lane row (`kind: 'surcharge'`). */
function SurchargesDetail({
  entries,
  cnyPerUsd,
  rowId,
}: {
  readonly entries: readonly UsageRequestUnitBillingChargedEntry[];
  readonly cnyPerUsd: number | null;
  readonly rowId: string;
}): JSX.Element {
  const t = useT();
  return (
    <div className="mt-3" data-testid={`requests-surcharges-${rowId}`}>
      <div className="mb-1 font-mono text-mono-label uppercase text-ink-low">
        {t('requests.surcharges.title')}
      </div>
      <ul className="m-0 flex list-none flex-col gap-1 p-0 font-mono text-ink-mid">
        {entries.map((entry, index) => (
          <li key={`${entry.unit}-${entry.variantKey}-${index}`}>
            {t('requests.unit_billing.charged', {
              quantity: entry.quantity,
              unit: t(`requests.unit_billing.unit.${entry.unit}`),
              amount: formatUsdMicrosOrCentsAsCnyMetered(entry.amountUsdMicros, 0, cnyPerUsd),
            })}
          </li>
        ))}
      </ul>
    </div>
  );
}

function isArchivedRow(row: UsageRequestLogEntry): boolean {
  return row.archived === true;
}

function archiveItemisationMissing(row: UsageRequestLogEntry): boolean {
  return (
    row.reasoningTokens === undefined &&
    row.imageInputTokens === undefined &&
    row.unitBilling === undefined &&
    row.tokenBilling === undefined
  );
}

interface ArchiveHydrate {
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly data: UsageRequestLogEntry | null | undefined;
}

type ItemisationMode =
  | { readonly kind: 'loading' }
  | { readonly kind: 'placeholder' }
  | { readonly kind: 'fields'; readonly row: UsageRequestLogEntry };

function resolveItemisation(
  listRow: UsageRequestLogEntry,
  hydrate: ArchiveHydrate | null,
): ItemisationMode {
  if (!isArchivedRow(listRow) || hydrate === null) {
    return { kind: 'fields', row: listRow };
  }
  if (hydrate.isLoading) return { kind: 'loading' };
  if (hydrate.isError) return { kind: 'placeholder' };
  const source = hydrate.data ?? listRow;
  if (archiveItemisationMissing(source)) return { kind: 'placeholder' };
  return { kind: 'fields', row: source };
}

function ArchivedOrLiveItemisation({
  listRow,
  hydrate,
  cnyPerUsd,
}: {
  readonly listRow: UsageRequestLogEntry;
  readonly hydrate: ArchiveHydrate | null;
  readonly cnyPerUsd: number | null;
}): JSX.Element | null {
  const t = useT();
  const mode = resolveItemisation(listRow, hydrate);
  if (mode.kind === 'loading') {
    return (
      <div className="sm:col-span-2 lg:col-span-4">
        <Skeleton className="h-16" testId={`requests-archive-detail-loading-${listRow.id}`} />
      </div>
    );
  }
  if (mode.kind === 'placeholder') {
    return (
      <p
        className="m-0 text-caption text-ink-low sm:col-span-2 lg:col-span-4"
        data-testid={`requests-archive-detail-placeholder-${listRow.id}`}
      >
        {t('requests.archived.detail_placeholder')}
      </p>
    );
  }
  const row = mode.row;
  return (
    <>
      {row.imageInputTokens !== undefined ? (
        <Detail
          label={t('requests.token_billing.dimension.image_input')}
          value={tokenCell(row, row.imageInputTokens)}
          testId={`requests-detail-image-input-${listRow.id}`}
        />
      ) : null}
      {row.reasoningTokens !== undefined ? (
        <Detail
          label={t('requests.token_billing.dimension.reasoning')}
          value={tokenCell(row, row.reasoningTokens)}
          testId={`requests-detail-reasoning-${listRow.id}`}
        />
      ) : null}
      {row.unitBilling !== undefined && row.unitBilling.kind !== 'surcharge' ? (
        <Detail
          label={t('requests.detail.unit_billing')}
          value={unitBillingSummary(row.unitBilling, cnyPerUsd, t)}
          testId={`requests-detail-unit-billing-${listRow.id}`}
        />
      ) : !isArchivedRow(listRow) &&
        row.unitBilling === undefined &&
        isUnitMeteredProtocol(row.protocol) ? (
        <Detail
          label={t('requests.detail.unit_billing')}
          value={t('requests.unit_billing.pending_breakdown')}
          testId={`requests-detail-unit-billing-${listRow.id}`}
        />
      ) : null}
    </>
  );
}

function ArchivedOrLiveBillingTables({
  listRow,
  hydrate,
  cnyPerUsd,
}: {
  readonly listRow: UsageRequestLogEntry;
  readonly hydrate: ArchiveHydrate | null;
  readonly cnyPerUsd: number | null;
}): JSX.Element | null {
  const mode = resolveItemisation(listRow, hydrate);
  if (mode.kind !== 'fields') return null;
  const row = mode.row;
  return (
    <>
      {row.tokenBilling !== undefined ? (
        <TokenBillingDetail billing={row.tokenBilling} cnyPerUsd={cnyPerUsd} rowId={listRow.id} />
      ) : null}
      {row.unitBilling?.kind === 'surcharge' ? (
        <SurchargesDetail
          entries={row.unitBilling.entries}
          cnyPerUsd={cnyPerUsd}
          rowId={listRow.id}
        />
      ) : null}
    </>
  );
}

function RequestTable({
  rows,
  expandedId,
  archiveDetailQ,
  cnyPerUsd,
  canReadOrgUsage,
  canFilterByKey,
  onToggle,
  onFilterByKey,
}: {
  readonly rows: readonly UsageRequestLogEntry[];
  readonly expandedId: string | null;
  readonly archiveDetailQ: ArchiveHydrate;
  readonly cnyPerUsd: number | null;
  readonly canReadOrgUsage: boolean;
  readonly canFilterByKey: boolean;
  readonly onToggle: (id: string) => void;
  readonly onFilterByKey: (apiKeyId: string) => void;
}): JSX.Element {
  const t = useT();
  return (
    <div className="overflow-x-auto">
      <table
        className="w-full min-w-[1180px] border-collapse text-body"
        data-testid="requests-table"
      >
        <thead>
          <tr className="border-b border-line text-left">
            <RequestTh>{t('requests.column.time')}</RequestTh>
            <RequestTh>{t('requests.column.request')}</RequestTh>
            <RequestTh>{t('requests.column.key')}</RequestTh>
            <RequestTh>{t('requests.column.model')}</RequestTh>
            <RequestTh>{t('requests.column.status')}</RequestTh>
            <RequestTh>{t('requests.column.duration')}</RequestTh>
            <RequestTh>{t('requests.column.first_byte')}</RequestTh>
            <RequestTh>{t('requests.column.input')}</RequestTh>
            <RequestTh>{t('requests.column.output')}</RequestTh>
            <RequestTh>{t('requests.column.cache_read')}</RequestTh>
            <RequestTh>{t('requests.column.cache_write')}</RequestTh>
            <RequestTh>{t('requests.column.cost')}</RequestTh>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isExpanded = expandedId === row.id;
            const modality = requestBillingModality(row.protocol, row.model);
            return (
              <Fragment key={row.id}>
                <tr
                  className="cursor-pointer border-b border-line-soft transition-colors duration-150 last:border-b-0 hover:bg-active/40"
                  data-testid={`requests-row-${row.id}`}
                  onClick={() => onToggle(row.id)}
                >
                  <td className="px-3 py-2 font-mono text-ink-low">
                    {formatTimestamp(row.createdAt)}
                  </td>
                  <td className="px-3 py-2 font-mono text-ink">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span>{shortRequestId(row.requestId)}</span>
                      {isArchivedRow(row) ? (
                        <Chip tone="neutral" lamp={false} testId={`requests-archived-${row.id}`}>
                          {t('requests.archived.badge')}
                        </Chip>
                      ) : null}
                    </span>
                  </td>
                  <td
                    className="px-3 py-2 font-medium text-ink"
                    data-testid={`requests-key-name-${row.id}`}
                  >
                    {apiKeyDisplayName(row.apiKeyName, row.apiKeyPrefix, t)}
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-mid">
                    <span className="flex flex-col gap-1">
                      <span className="flex items-center gap-1.5">
                        <RequestModalityIcon modality={modality} rowId={row.id} />
                        <span>{row.model}</span>
                      </span>
                      <span className="text-mono-label text-ink-low">{row.vendor}</span>
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <StatusChip
                      variant={row.statusCode >= 400 ? 'error' : 'ok'}
                      label={String(row.statusCode)}
                      testId={`requests-status-${row.id}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-mid">
                    {row.latencyMs === null
                      ? '—'
                      : t('requests.duration.ms', { value: row.latencyMs })}
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-mid">
                    {row.clientResponseElapsedMs === null
                      ? '—'
                      : t('requests.duration.ms', { value: row.clientResponseElapsedMs })}
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-mid">
                    {tokenCell(row, row.inputTokens)}
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-mid">
                    {tokenCell(row, row.outputTokens)}
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-mid">
                    {tokenCell(row, row.cacheReadTokens)}
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-mid">
                    {tokenCell(row, row.cacheWriteTokens)}
                  </td>
                  <td className="px-3 py-2 font-mono text-forest">
                    {isRatingPending(row) ? (
                      <span className="text-ink-low">{t('requests.cost.pending')}</span>
                    ) : row.unitBilling?.kind === 'not_charged' ? (
                      // Wording is reason-specific on purpose: `not_delivered`
                      // is final ("Not charged"), while `pricing_review` is a
                      // reversible hold — saying "not charged" there would
                      // promise an outcome the review may still change.
                      <span
                        className="text-ink-low"
                        data-testid={`requests-cost-not-charged-${row.id}`}
                      >
                        {t(`requests.cost.not_charged.${row.unitBilling.reason}`)}
                      </span>
                    ) : (
                      formatUsdMicrosOrCentsAsCnyMetered(
                        row.costUsdMicros,
                        row.costUsdCents,
                        cnyPerUsd,
                      )
                    )}
                  </td>
                </tr>
                {isExpanded ? (
                  <tr key={`${row.id}-details`} data-testid={`requests-row-details-${row.id}`}>
                    <td colSpan={12} className="border-b border-line-soft bg-raised px-3 py-3">
                      <dl className="m-0 grid gap-3 text-caption sm:grid-cols-2 lg:grid-cols-4">
                        <RequestIdDetail requestId={row.requestId} rowId={row.id} />
                        <Detail label={t('requests.detail.api_key_id')} value={row.apiKeyId} />
                        <Detail label={t('requests.detail.protocol')} value={row.protocol} />
                        <Detail
                          label={t('requests.detail.billing')}
                          value={t(`requests.detail.billing.${billingState(row)}`)}
                          testId={`requests-detail-billing-${row.id}`}
                        />
                        <ArchivedOrLiveItemisation
                          listRow={row}
                          hydrate={isArchivedRow(row) ? archiveDetailQ : null}
                          cnyPerUsd={cnyPerUsd}
                        />
                        <Detail
                          label={t('requests.detail.error_category')}
                          value={statusCategory(row.statusCode, t)}
                          testId={`requests-detail-error-category-${row.id}`}
                        />
                      </dl>
                      <ArchivedOrLiveBillingTables
                        listRow={row}
                        hydrate={isArchivedRow(row) ? archiveDetailQ : null}
                        cnyPerUsd={cnyPerUsd}
                      />
                      {/*
                        A log entry is a lead, not a destination. Two
                        onward moves, deliberately different: narrowing
                        THIS log to the key stays here, while the key's
                        own history is a question `/usage` answers.
                      */}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {canFilterByKey ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => onFilterByKey(row.apiKeyId)}
                            data-testid={`requests-filter-by-key-${row.id}`}
                          >
                            {t('requests.detail.filter_by_key')}
                          </Button>
                        ) : null}
                        {canReadOrgUsage ? (
                          <Link
                            to={`/usage?apiKeyId=${encodeURIComponent(row.apiKeyId)}`}
                            className={buttonClass('secondary', 'sm')}
                            data-testid={`requests-view-key-usage-${row.id}`}
                          >
                            {t('requests.detail.view_key_usage')}
                          </Link>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RequestTh({ children }: { readonly children: string }): JSX.Element {
  return (
    <th className="px-3 py-2 font-mono text-mono-label font-medium uppercase text-ink-low">
      {children}
    </th>
  );
}

function Detail({
  label,
  value,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly testId?: string;
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-1" data-testid={testId}>
      <dt className="font-mono text-mono-label uppercase text-ink-low">{label}</dt>
      <dd className="m-0 break-all font-mono text-ink-mid">{value}</dd>
    </div>
  );
}

/**
 * The request-id detail with a one-click copy — a request id exists to
 * be pasted into a support ticket, and hand-selecting a break-all mono
 * string is exactly the interaction that drops characters. Success
 * flips the icon to a check for `useClipboard`'s reset window; a
 * clipboard failure reports inline (the hook has already fallen back
 * to selecting the text, so manual copy still works).
 */
function RequestIdDetail({
  requestId,
  rowId,
}: {
  readonly requestId: string;
  readonly rowId: string;
}): JSX.Element {
  const t = useT();
  const { copy, copied, error } = useClipboard();
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dt className="font-mono text-mono-label uppercase text-ink-low">
        {t('requests.detail.request_id')}
      </dt>
      <dd className="m-0 flex min-w-0 flex-col gap-1">
        <span className="flex min-w-0 items-center gap-1">
          <span className="break-all font-mono text-ink-mid">{requestId}</span>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('requests.detail.copy_request_id')}
            onClick={() => {
              void copy(requestId);
            }}
            data-testid={`requests-copy-request-id-${rowId}`}
          >
            {copied ? (
              <Check aria-hidden="true" className="h-3.5 w-3.5 text-ok" />
            ) : (
              <Copy aria-hidden="true" className="h-3.5 w-3.5" />
            )}
          </Button>
        </span>
        {error !== null ? (
          <span
            role="alert"
            data-testid={`requests-copy-request-id-error-${rowId}`}
            className="font-mono text-mono-label text-bad"
          >
            {t(clipboardErrorKey(error))}
          </span>
        ) : null}
      </dd>
    </div>
  );
}

function shortRequestId(value: string): string {
  if (value.length <= 18) return value;
  return `${value.slice(0, 10)}…${value.slice(-6)}`;
}
